import {
  faCircleCheck,
  faCircleExclamation,
  faEye,
  faEyeSlash,
  faWarning,
} from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { PrivateKeyRecoveryOptionStatusEnum } from '#src/__generated__/globalTypes.ts';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyM, BodyS, HeadlineXS } from '#src/atoms/typography/index.tsx';
import {
  SecurityCheckTab,
  useAccountSecurityCheckContext,
} from '#src/contexts/accountSecurityCheck/index.ts';
import type { RecoveryOption } from '#src/hooks/recovery/useRecoveryOptions.ts';
import { useRecoveryOptions as useRecoveryEmails } from '#src/hooks/recovery/useRecoveryOptions.ts';
import { useResendVerificationCodeForRecoveryEmail } from '#src/hooks/recovery/useResendVerificationCodeForRecoveryEmail.ts';
import { useToggle } from '#src/hooks/useToggle.ts';
import { glossary } from '#src/lib/glossary.ts';
import { hideEmailDetails } from '#src/lib/privacy.ts';

const Email = styled(BodyM)`
  display: flex;
  gap: var(--unit);
  align-items: center;
`;

const EmailItem = ({ email }: { email: string }) => {
  const [show, toggleShow] = useToggle(false);

  return (
    <Email color="var(--c-white)">
      {show ? email : hideEmailDetails(email)}
      <button type="button" onClick={toggleShow}>
        <FontAwesomeIcon
          icon={show ? faEyeSlash : faEye}
          size="xs"
          color="var(--c-white)"
        />
      </button>
    </Email>
  );
};

export const RecoveryEmail = () => {
  const { setOnBackTarget, setSecurityCheckTab, setUnverifiedRecoveryEmail } =
    useAccountSecurityCheckContext();
  const { resendVerificationCodeForRecoveryEmail } =
    useResendVerificationCodeForRecoveryEmail();
  const { recoveryEmails, allActiveRecoveryEmailsArePrivateRelay } =
    useRecoveryEmails();
  const hasRecoveryEmail = !!recoveryEmails.length;
  const hasActiveRecoveryEmail = recoveryEmails.find(
    option => option.status === PrivateKeyRecoveryOptionStatusEnum.ACTIVE
  );

  const resendCode = (option: RecoveryOption) => {
    resendVerificationCodeForRecoveryEmail(option.id);
    setOnBackTarget(SecurityCheckTab.HOME);
    setSecurityCheckTab(SecurityCheckTab.VERIFY_RECOVERY_EMAIL);
    setUnverifiedRecoveryEmail(option.destination);
  };

  return (
    <Vertical>
      <Horizontal>
        {hasActiveRecoveryEmail ? (
          allActiveRecoveryEmailsArePrivateRelay ? (
            <FontAwesomeIcon icon={faWarning} color="var(--c-yellow-500)" />
          ) : (
            <FontAwesomeIcon
              icon={faCircleCheck}
              color="var(--c-onBase-success)"
            />
          )
        ) : (
          <FontAwesomeIcon
            icon={faCircleExclamation}
            color="var(--c-red-500)"
          />
        )}
        <HeadlineXS as="h6" color="var(--c-white)">
          <FormattedMessage defaultMessage="Recovery email(s)" />
        </HeadlineXS>
      </Horizontal>
      {!hasRecoveryEmail && (
        <BodyS color="var(--c-nd-600)">
          <FormattedMessage defaultMessage="We strongly recommend adding a recovery email. It will help us contact you if you get locked out and assist in recovering your Sorare wallet." />
        </BodyS>
      )}
      {!!recoveryEmails.length && (
        <Vertical gap={2}>
          {recoveryEmails.map(email => (
            <div key={email.destination}>
              <EmailItem email={email.destination} />
              {email.status ===
                PrivateKeyRecoveryOptionStatusEnum.PENDING_VALIDATION && (
                <>
                  <BodyS color="var(--c-nd-600)">
                    <FormattedMessage
                      defaultMessage="{icon} Pending verification"
                      values={{
                        icon: (
                          <FontAwesomeIcon
                            icon={faCircleExclamation}
                            color="var(--c-red-500)"
                          />
                        ),
                      }}
                    />
                  </BodyS>
                  <BodyS color="var(--c-link)">
                    <button type="button" onClick={() => resendCode(email)}>
                      <FormattedMessage {...glossary.resendVerificationCode} />
                    </button>
                  </BodyS>
                </>
              )}
            </div>
          ))}
          {allActiveRecoveryEmailsArePrivateRelay ? (
            <BodyS color="var(--c-yellow-500)">
              <FormattedMessage defaultMessage="Your recovery email is an Apple Private Relay address, which can be deactivated by Apple at any time. We recommend adding a personal email to ensure you can always recover your wallet." />
            </BodyS>
          ) : null}
        </Vertical>
      )}
      <BodyM color="var(--c-link)">
        <button
          type="button"
          onClick={() => {
            setSecurityCheckTab(SecurityCheckTab.ADD_RECOVERY_EMAIL);
            setUnverifiedRecoveryEmail(null);
          }}
        >
          {hasRecoveryEmail ? (
            <FormattedMessage defaultMessage="Add another recovery email" />
          ) : (
            <FormattedMessage defaultMessage="Add recovery email" />
          )}
        </button>
      </BodyM>
    </Vertical>
  );
};
